import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { GenericQuestion } from 'types/genericQuestion';
import { fetchGenericQuestionsByParams } from 'services/genericQuestion';
import { ZCDSelect } from '@zencity/common-ui';
import { SelectOption } from '@zencity/common-ui/lib/ZCD/ZCDFilter/types';
import { useTranslation } from 'react-i18next';
import { ReportFilterProps } from 'screens/WidgetGenerator/reportGeneratorComponents/types';
import { SurveysReportGeneratorFilterKey } from 'screens/WidgetGenerator/reportGeneratorComponents/enums';
import { FieldTitle } from 'screens/WidgetGenerator/components/FieldTitle/FieldTitle';

interface GenericQuestionsById {
  [genericQuestionId: string]: GenericQuestion;
}

export const ClassifiedGenericQuestionsFilter = (props: ReportFilterProps<number>): ReactElement | null => {
  const { value, filterValues, onChange } = props;
  const [genericQuestions, setGenericQuestions] = useState<GenericQuestionsById>({});
  const { t: translate } = useTranslation();

  const selectedSurveyGroupId = filterValues?.[SurveysReportGeneratorFilterKey.SURVEY_GROUP];

  useEffect(() => {
    const fetchData = async (surveyGroupId: number) => {
      const response = await fetchGenericQuestionsByParams({
        survey_group: surveyGroupId,
        enrich_with_sub_topic: true,
      });

      const restructuredById = response.results.reduce(
        (accum, instance) => ({ ...accum, [instance.id]: instance }),
        {} as GenericQuestionsById,
      );
      setGenericQuestions(restructuredById);

      return response;
    };

    if (selectedSurveyGroupId) {
      fetchData(selectedSurveyGroupId);
    }
  }, [selectedSurveyGroupId]);

  const handleClassifiedQuestionSelection = (newValue: unknown) => {
    const selectedValue = newValue as SelectOption;
    onChange(selectedValue.value as number);
  };

  const genericQuestionsOptions: SelectOption[] = useMemo(
    () =>
      Object.values(genericQuestions).map((instance) => ({
        value: instance.id,
        label: instance.text,
      })),
    [genericQuestions],
  );

  const selectedOption = useMemo(
    () => genericQuestionsOptions.find((option) => option.value === value),
    [genericQuestionsOptions, value],
  );

  return (
    <>
      <FieldTitle title={translate('widgetGenerator.selectQuestion')} />
      <ZCDSelect
        placeholder={translate('widgetGenerator.selectQuestion')}
        options={genericQuestionsOptions}
        onChange={handleClassifiedQuestionSelection}
        value={selectedOption}
        isDisabled={Object.keys(genericQuestions).length < 1}
      />
    </>
  );
};
